/**
 * KAN-649: сообщение с Telegram-аккаунта. Режим "any" - получатель (@username или телефон)
 * и текст, режим "self" - только текст, адрес берётся из настроек канала (test_recipient).
 * Отправка ставится в очередь: уходит держателем сессии или воркером аккаунта.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { describeApiError } from '@/lib/telegramConnect';
import {
  NEW_CHAT_WARNING,
  QUEUED_TOAST,
  SELF_RECIPIENT,
  SEND_ERROR,
  TEXT_MAX,
  defaultSelfText,
  validateSend,
  type SendMode,
} from '@/lib/telegramDirectSend';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

interface Props {
  account: TelegramAccount;
  mode: SendMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function errorText(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return describeApiError(err, SEND_ERROR);
}

export function AccountSendDialog({ account, mode, open, onOpenChange }: Props): JSX.Element {
  const key = `${account.operator_id}-${account.account_id}`;
  const [recipient, setRecipient] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selfText = defaultSelfText(account);

  useEffect(() => {
    if (!open) return;
    setRecipient('');
    setText(mode === 'self' ? selfText : '');
    setError(null);
  }, [open, mode, selfText]);

  const send = useMutation({
    mutationFn: () =>
      telegramAccountsApi.sendMessage(
        account.account_id,
        { recipient: mode === 'self' ? SELF_RECIPIENT : recipient.trim(), text: text.trim() },
        account.operator_id
      ),
    onSuccess: () => {
      toast.success(QUEUED_TOAST);
      onOpenChange(false);
    },
    onError: (err) => setError(errorText(err)),
  });

  const onSubmit = (): void => {
    const problem = validateSend(mode, recipient, text);
    setError(problem);
    if (!problem) send.mutate();
  };

  const title = mode === 'self' ? 'Отправить себе' : 'Написать с аккаунта';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`send-dialog-${key}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'self'
              ? 'Сообщение уйдёт на ваш Telegram для проверки из настроек канала.'
              : `Отправитель: ${account.label || account.account_id}`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {mode === 'any' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor={`send-recipient-${key}`}>Получатель</Label>
              <Input
                id={`send-recipient-${key}`}
                data-testid={`send-recipient-${key}`}
                placeholder="@username или +79001234567"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label htmlFor={`send-text-${key}`}>Текст</Label>
            <Textarea
              id={`send-text-${key}`}
              data-testid={`send-text-${key}`}
              rows={4}
              maxLength={TEXT_MAX}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <p className="text-xs text-amber-700" data-testid={`send-warning-${key}`}>
            {NEW_CHAT_WARNING}
          </p>
          {error && (
            <p className="text-xs text-red-700" data-testid={`send-error-${key}`}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            data-testid={`send-submit-${key}`}
            disabled={send.isPending}
            onClick={onSubmit}
          >
            {send.isPending ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Кнопки "Написать" и "Отправить себе" в строке аккаунта. */
export function AccountSendActions({ account }: { account: TelegramAccount }): JSX.Element {
  const key = `${account.operator_id}-${account.account_id}`;
  const [mode, setMode] = useState<SendMode | null>(null);
  const btn = 'h-7 px-2 text-xs';
  return (
    <div className="mt-1.5 flex gap-1.5">
      <Button
        variant="outline"
        size="sm"
        className={btn}
        data-testid={`send-any-${key}`}
        onClick={() => setMode('any')}
      >
        Написать
      </Button>
      <Button
        variant="outline"
        size="sm"
        className={btn}
        data-testid={`send-self-${key}`}
        onClick={() => setMode('self')}
      >
        Отправить себе
      </Button>
      <AccountSendDialog
        account={account}
        mode={mode ?? 'any'}
        open={mode !== null}
        onOpenChange={(o) => !o && setMode(null)}
      />
    </div>
  );
}
